import { FEATURES } from './config';
import { chf } from './format';
import { parseAmount, formatThousands } from './format';
import { VERGLEICH_DISCLAIMER, CONSENT_TEXT_VERSION } from './copy';
import { fetchRates, type PublicRates, type RateProduct } from './rates';
import { buildComparison, clampVolume, VOLUME_DEFAULT } from './mehrkosten';
import { renderTurnstile, type TurnstileWidget } from './turnstile';
import { submitBeratung, ApiError } from './api';
import { attributionFuerSubmit } from './attribution';
import { track, bindFieldTouch, amountBucket } from './analytics';
import type { BeratungPayload } from './types';

const CTA_DEFAULT = 'Top-Zins sichern – kostenlose & unverbindliche Beratung';
const CTA_ANFRAGEN = 'Aktuelle Top-Konditionen anfragen';

const INFO_TEXT =
  'Mehrkosten pro Jahr = (Zinssatz des Anbieters − Finavest Top-Zins) × Hypothekarvolumen, ' +
  'gerundet auf CHF 50. Ist kein Finavest-Zinssatz publiziert, dient der günstigste ' +
  'angezeigte Anbieter als Vergleichsbasis.';

const isEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const apiErrorReason = (err: unknown): string =>
  err instanceof ApiError ? (err.status ? `http_${err.status}` : 'network') : 'unknown';

/** "1.45%" (de-CH, max 2 decimals). Rates are already percent values. */
const ratePct = (p: number): string =>
  p.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

/** "12.06.2026, 06:00 Uhr" */
const standLabel = (iso: string): string =>
  'Stand: ' +
  new Date(iso).toLocaleString('de-CH', { dateStyle: 'medium', timeStyle: 'short' }) +
  ' Uhr';

class Vergleich {
  private opened = false;
  private rates: PublicRates | null = null;
  private productKey = '';
  private volume = VOLUME_DEFAULT;

  private tsWidget: TurnstileWidget | undefined;
  /** One Beratung submit in flight (RV616 m3) — see onSubmit. */
  private busy = false;
  private renderTimer: ReturnType<typeof setTimeout> | undefined;
  private volumeTrackTimer: ReturnType<typeof setTimeout> | undefined;
  private lastVolumeBucket = '';

  constructor(private readonly switchToRechner: () => void) {}

  /** Called by the tab controller on every activation of the Zinsvergleich tab. */
  onShow(): void {
    if (!this.opened) {
      this.opened = true;
      track('vergleich_view');
      void this.load();
      return;
    }
    // A failed load is not memoized (rates.ts caches success only) — retry at
    // most once per re-activation; loaded rates stay for the page lifetime.
    if (this.rates === null) void this.load();
  }

  init(): void {
    this.bindControls();
    this.bindInfoToggle();
    this.bindBeratung();
    this.bindBridge();
    this.bindZinsupdate();
  }

  // ---------- data ----------

  private async load(): Promise<void> {
    // Reset for the retry path (no-op on the first load).
    this.el('vg-loading').hidden = false;
    this.el('vg-empty').hidden = true;
    this.rates = await fetchRates();
    this.el('vg-loading').hidden = true;
    if (!this.rates) {
      // Unreachable/empty feed: honest empty state, CTA still works. Old rates
      // are never shown (no client-side persistence by design).
      this.el('vg-empty').hidden = false;
      this.el('vg-controls-wrap').hidden = true;
      this.text('vg-beratung-open', CTA_ANFRAGEN);
      return;
    }
    this.el('vg-controls-wrap').hidden = false;
    const select = this.el<HTMLSelectElement>('vg-product');
    select.innerHTML = '';
    for (const p of this.rates.products) {
      const opt = document.createElement('option');
      opt.value = p.key;
      opt.textContent = p.label;
      select.appendChild(opt);
    }
    this.productKey = this.rates.products[0]?.key ?? '';
    select.value = this.productKey;
    this.el('vg-table-wrap').hidden = false;
    this.render();
  }

  private product(): RateProduct | null {
    return this.rates?.products.find((p) => p.key === this.productKey) ?? null;
  }

  // ---------- rendering ----------

  private render(): void {
    const product = this.product();
    if (!product || !this.rates) return;

    const cmp = buildComparison(product, this.volume);
    const stale = cmp.finavestRatePct === null;
    // `fresh: false` = the Finavest rate sits in the contract's 7–10-day
    // twilight zone → asterisk on the anchor row + footnote on the Stand line.
    const freshCaveat = !stale && product.finavestFresh === false;
    const tbody = this.el('vg-rows');
    tbody.innerHTML = '';

    // Row 1: Finavest, highlighted. Stale → "auf Anfrage" + inline CTA link in
    // ONE cell spanning both numeric columns (there are no Mehrkosten without
    // a Finavest rate, and a nowrap "Top-Zins auf Anfrage" in a third of the
    // card used to push the table past the card edge).
    const fin = document.createElement('tr');
    fin.className = 'vg-row vg-row--finavest';
    if (stale) {
      this.addCells(fin, 'Finavest', 'Top-Zins auf Anfrage');
      const rateCell = fin.children[1] as HTMLTableCellElement;
      rateCell.colSpan = 2;
      rateCell.classList.add('vg-num--ask');
      const link = document.createElement('button');
      link.type = 'button';
      link.className = 'vg-row__ask';
      link.textContent = 'jetzt anfragen';
      link.addEventListener('click', () => this.openBeratung());
      (rateCell.firstElementChild as HTMLElement).appendChild(link);
    } else {
      this.addCells(
        fin,
        'Finavest',
        `Top-Zins ab ${ratePct(cmp.finavestRatePct as number)}${freshCaveat ? ' *' : ''}`,
        chf(0),
      );
    }
    tbody.appendChild(fin);

    for (const row of cmp.rows) {
      const tr = document.createElement('tr');
      tr.className = 'vg-row';
      this.addCells(tr, row.name, ratePct(row.ratePct), chf(row.mehrkostenChf));
      tbody.appendChild(tr);
    }

    const freshNote = freshCaveat ? ' · * Stand älter als 7 Tage' : '';
    this.text('vg-stand', standLabel(this.rates.stand) + freshNote);
    this.text('vg-disclaimer', VERGLEICH_DISCLAIMER);
    this.text('vg-beratung-open', stale ? CTA_ANFRAGEN : CTA_DEFAULT);
  }

  /**
   * Cells carry data-label so the mobile card layout can show per-cell
   * captions; the value sits in its own span so that layout (a flex row of
   * caption + value) can let a long value wrap instead of overflowing.
   */
  private addCells(tr: HTMLTableRowElement, ...vals: string[]): void {
    const labels = ['Anbieter', 'Zinssatz', 'Mehrkosten p.a.'];
    vals.forEach((v, i) => {
      const td = document.createElement('td');
      const val = document.createElement('span');
      val.className = 'vg-val';
      val.textContent = v;
      td.appendChild(val);
      td.dataset.label = labels[i] ?? '';
      if (i > 0) td.className = 'vg-num';
      tr.appendChild(td);
    });
  }

  // ---------- controls ----------

  private bindControls(): void {
    this.el<HTMLSelectElement>('vg-product').addEventListener('change', () => {
      this.productKey = this.el<HTMLSelectElement>('vg-product').value;
      this.render();
      track('vergleich_product_change', { product: this.productKey });
    });

    const vol = this.el<HTMLInputElement>('vg-volume');
    vol.addEventListener('input', () => {
      const n = parseAmount(vol.value);
      if (n > 0) this.volume = n;
      // Debounced recompute — typing "1" must not flash "CHF 100'000" tables.
      clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => this.render(), 300);
      // Debounced, bucketed analytics (never the raw amount), only on change.
      clearTimeout(this.volumeTrackTimer);
      this.volumeTrackTimer = setTimeout(() => {
        const bucket = amountBucket(clampVolume(this.volume));
        if (bucket === this.lastVolumeBucket) return;
        this.lastVolumeBucket = bucket;
        track('vergleich_volume_change', { volume_bucket: bucket });
      }, 800);
    });
    vol.addEventListener('focus', () => {
      const n = parseAmount(vol.value);
      vol.value = n > 0 ? String(n) : '';
    });
    vol.addEventListener('blur', () => {
      this.volume = clampVolume(parseAmount(vol.value) || VOLUME_DEFAULT);
      vol.value = formatThousands(this.volume);
      clearTimeout(this.renderTimer);
      this.render();
    });
  }

  private bindInfoToggle(): void {
    const btn = this.el<HTMLButtonElement>('vg-info-btn');
    const info = this.el('vg-info');
    info.textContent = INFO_TEXT;
    btn.addEventListener('click', () => {
      const open = info.hidden;
      info.hidden = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  // ---------- Beratung (same funnel step as the Rechner, own instance) ----------

  private bindBeratung(): void {
    this.el('vg-beratung-open').addEventListener('click', () => this.openBeratung());
    this.el<HTMLFormElement>('vg-beratung-form').addEventListener(
      'submit',
      (e) => void this.onSubmit(e),
    );
    // Per-field engagement (GTM funnel drop-off) — fire-only, NEVER the value.
    bindFieldTouch('vergleich_beratung_field', [
      { field: 'vorname', el: document.getElementById('vb-first') },
      { field: 'nachname', el: document.getElementById('vb-last') },
      { field: 'email', el: document.getElementById('vb-email') },
      { field: 'telefon', el: document.getElementById('vb-phone') },
      { field: 'zeit', el: document.getElementById('vb-time') },
      { field: 'sprache', el: document.getElementById('vb-lang') },
      { field: 'consent', el: document.getElementById('vb-consent') },
    ]);
  }

  private openBeratung(): void {
    track('vergleich_beratung_click');
    this.el('vg-beratung-open').hidden = true;
    const form = this.el('vg-beratung-form');
    form.hidden = false;
    if (!this.tsWidget) {
      void renderTurnstile(this.el('ts-vergleich'), 'finavest-beratung')
        .then((w) => (this.tsWidget = w))
        .catch(() => undefined);
    }
    form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  private async onSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    // RV616 m3: one submit in flight — the consent question below can wait up
    // to CONSENT_TIMEOUT_MS, and a second click must not send the same
    // Turnstile token again.
    if (this.busy) return;
    track('vergleich_beratung_submit');
    const first = this.val('vb-first');
    const last = this.val('vb-last');
    const email = this.val('vb-email');
    const phone = this.val('vb-phone');

    // Machine-code reasons only — never echo user input into analytics.
    if (!first || !last) return this.fail('missing_name', 'Bitte geben Sie Vor- und Nachnamen an.');
    if (!isEmail(email))
      return this.fail('invalid_email', 'Bitte geben Sie eine gültige E-Mail-Adresse an.');
    if (!phone) return this.fail('missing_phone', 'Bitte geben Sie eine Telefonnummer an.');
    if (!/^[+0-9 ()/.-]{6,32}$/.test(phone))
      return this.fail('invalid_phone', 'Bitte geben Sie eine gültige Telefonnummer an.');
    if (!this.el<HTMLInputElement>('vb-consent').checked)
      return this.fail('missing_consent', 'Bitte bestätigen Sie die Einwilligung.');
    const token = this.tsWidget?.getToken() ?? '';
    if (!token)
      return this.fail('missing_turnstile', 'Bitte bestätigen Sie, dass Sie kein Roboter sind.');
    this.err('');

    // Locked BEFORE the consent question, not after it (RV616 m3).
    this.busy = true;
    const btn = this.el<HTMLButtonElement>('vb-btn');
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Senden…';
    try {
      // RV614 M3: the click id only when the host says the ads choice was not refused.
      const utm = await attributionFuerSubmit(location.search);
      const payload: BeratungPayload = {
        turnstileToken: token,
        firstName: first,
        lastName: last,
        email,
        phone,
        address: { street: '', zip: '', city: '' },
        preferredTime: (this.val('vb-time') || 'egal').slice(0, 30),
        language: this.val('vb-lang') || 'de',
        triggerSource: 'vergleich',
        // Unlike Rechner step 3 there is no preceding lead submit that recorded
        // consent — the checked box above IS the consent moment, so the version
        // travels along (same const as the lead gate's consent.textVersion).
        consentVersion: CONSENT_TEXT_VERSION,
        // No preceding lead submit either: this request MINTS the lead, so the
        // campaign attribution rides here (utm_* + Google click ids).
        ...(utm ? { utm } : {}),
      };
      await submitBeratung(payload);
      track('vergleich_beratung_success');
      this.el('vg-beratung-form').hidden = true;
      this.el('vg-beratung-success').hidden = false;
      this.el('vg-beratung-success').scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (err) {
      track('vergleich_beratung_error', { reason: apiErrorReason(err) });
      this.err(err instanceof ApiError ? err.message : 'Es ist ein Fehler aufgetreten.');
      this.tsWidget?.reset();
    } finally {
      this.busy = false;
      btn.disabled = false;
      btn.textContent = label;
    }
  }

  // ---------- bridge + Zins-Update ----------

  private bindBridge(): void {
    this.el('vg-to-rechner').addEventListener('click', () => {
      track('vergleich_to_rechner');
      this.switchToRechner();
    });
  }

  private bindZinsupdate(): void {
    if (!FEATURES.zinsupdate) return; // default OFF — no dead submit in production
    this.el('vg-zinsupdate').hidden = false;
    this.el<HTMLFormElement>('vg-zinsupdate-form').addEventListener('submit', (e) => {
      e.preventDefault();
      // W6c wires the real signup (backend + double-opt-in). Behind the flag,
      // surface that honestly instead of pretending to subscribe.
      const err = this.el('vz-error');
      err.textContent = 'Die Anmeldung ist in Kürze verfügbar.';
      err.hidden = false;
    });
  }

  // ---------- small DOM utilities ----------

  private el<T extends HTMLElement = HTMLElement>(id: string): T {
    const node = document.getElementById(id);
    if (!node) throw new Error(`#${id} missing`);
    return node as T;
  }
  private text(id: string, value: string): void {
    this.el(id).textContent = value;
  }
  private val(id: string): string {
    return (this.el(id) as HTMLInputElement | HTMLSelectElement).value.trim();
  }
  private err(msg: string): void {
    const e = this.el('vb-error');
    e.textContent = msg;
    e.hidden = !msg;
  }
  private fail(reason: string, msg: string): void {
    track('vergleich_beratung_error', { reason });
    this.err(msg);
  }
}

export function initVergleich(switchToRechner: () => void): { onShow(): void } {
  const v = new Vergleich(switchToRechner);
  v.init();
  return { onShow: () => v.onShow() };
}
